// Field position tracking (2026-10-05, AR stability). DOM glue around the
// pure arTrack.PositionFilter: GPS fixes from the field app's
// watchPosition, steps and the stationary detector from DeviceMotion
// (heading.ts onMotion), calibration anchors and "hold position" from the
// AR view. The AR overlay draws from trackedFix() instead of the raw fix.
//
// Battery: no extra sensors - DeviceMotion is already on for the heading's
// gyro; the filter is a handful of multiplications per event.
import { LocalFrame, PositionFilter, StepDetector } from "./arTrack";
import type { MotionState } from "./arTrack";
import { getHeading, onMotion } from "./heading";

export interface TrackedFix {
  lat: number;
  lon: number;
  /** 1-sigma position uncertainty of the filtered position, metres */
  accuracy: number;
  /** the last raw GPS accuracy */
  gpsAccuracy: number | null;
  held: boolean;
  motion: MotionState;
}

/** Undo information for one calibration anchor. */
export interface AnchorToken {
  x: number[];
  P: number[];
  after: number[];
  afterPxx: number;
}

/** A held position is released after this many steps (you walked off). */
const HOLD_RELEASE_STEPS = 6;
/** A fix this far from the frame's origin starts a new frame. */
const FRAME_RESET_M = 5000;

/** while camera tracking reported movement this recently, steps don't also dead-reckon */
const VO_OWNS_S = 0.7;
/** faster than this (m/s) is not the walker: a passing car filling the view */
const VO_MAX_SPEED = 3;

const T: {
  voAt: number;
  voT: number;
  voRejects: number;
  frame: LocalFrame | null;
  f: PositionFilter;
  steps: StepDetector;
  lastFixAt: number;
  headingOffset: number;
  started: boolean;
  heldSteps: number;
} = {
  voAt: -Infinity, voT: -Infinity, voRejects: 0,
  frame: null, f: new PositionFilter(), steps: new StepDetector(), lastFixAt: -1, headingOffset: 0, started: false, heldSteps: 0,
};
const events = new Set<(e: "hold-released") => void>();

function now(): number {
  return performance.now() / 1000;
}

/** Start counting steps (after startHeading() got the motion permission). */
export function startTracking(): void {
  if (T.started) return;
  T.started = true;
  onMotion((ev) => {
    const a = ev.accelerationIncludingGravity;
    if (!a || a.x === null || a.y === null || a.z === null) return;
    const t = now();
    const r = T.steps.feed(t, a.x, a.y, a.z);
    if (r.state !== T.f.motion) T.f.setMotion(r.state, t);
    if (!r.step) return;
    if (T.f.held) {
      if (++T.heldSteps >= HOLD_RELEASE_STEPS) {
        T.f.setHold(false, t);
        T.heldSteps = 0;
        for (const fn of events) fn("hold-released");
      }
      return;
    }
    // camera tracking measures the movement itself (and its direction): no double counting
    if (t - T.voAt < VO_OWNS_S) return;
    const hd = getHeading();
    // the phone is held up in front, so the camera heading is the walking
    // direction (wrong if you walk sideways looking at something)
    if (hd.heading !== null) T.f.step(t, hd.heading + T.headingOffset);
  });
}

/** Camera tracking (visualOdometry.ts, experimental): the phone moved
 * (de, dn) metres in the RAW compass frame since the last result, at
 * `quality`. The calibration's heading correction is applied here, as for
 * steps. A lost frame or an implausible speed is ignored, and steps take
 * over again VO_OWNS_S later. */
export function trackOdometry(r: { de: number; dn: number; quality: "good" | "weak" | "lost" }): void {
  if (r.quality === "lost" || !Number.isFinite(r.de) || !Number.isFinite(r.dn)) return;
  const t = now();
  const d = Math.hypot(r.de, r.dn);
  const dt = Math.max(t - T.voT, 1 / 30);
  T.voT = t;
  if (d / dt > VO_MAX_SPEED) {
    T.voRejects++;
    return;
  }
  const a = (T.headingOffset * Math.PI) / 180;
  const c = Math.cos(a), s = Math.sin(a);
  if (!T.f.held && d > 0) T.f.odometry(t, r.de * c + r.dn * s, r.dn * c - r.de * s);
  T.voAt = t;
}

/** Camera tracking switched off: steps take over at once. */
export function stopOdometry(): void {
  T.voAt = -Infinity;
}

/** Every GPS fix from watchPosition. */
export function trackGps(fix: { lat: number; lon: number; accuracy: number; at?: number }): void {
  if (fix.at !== undefined && fix.at === T.lastFixAt) return; // maximumAge re-delivery
  T.lastFixAt = fix.at ?? -1;
  if (T.frame) {
    const [e, n] = T.frame.toLocal(fix.lat, fix.lon);
    if (Math.hypot(e, n) > FRAME_RESET_M) {
      T.frame = null;
      const motion = T.f.motion;
      T.f = new PositionFilter();
      T.f.motion = motion;
    }
  }
  if (!T.frame) T.frame = new LocalFrame(fix.lat, fix.lon);
  const [e, n] = T.frame.toLocal(fix.lat, fix.lon);
  T.f.gps(now(), e, n, fix.accuracy);
}

/** The filter's own estimate now, in its local frame and as lat/lon (AR
 * scan mode records it with each frame; its ring buffer, scanCore.EstimateRing,
 * is what a late scan fix will be compared against). Null before the first fix. */
export function trackEstimate(): { t: number; e: number; n: number; sigma: number; lat: number; lon: number;
  originLat: number; originLon: number; held: boolean; motion: MotionState } | null {
  if (!T.frame || !T.f.ready) return null;
  const t = now();
  const est = T.f.estimate(t);
  const [lat, lon] = T.frame.toLatLon(est.e, est.n);
  const [originLat, originLon] = T.frame.toLatLon(0, 0);
  return { t, e: est.e, n: est.n, sigma: est.sigma, lat, lon, originLat, originLon, held: est.held, motion: est.motion };
}

export function trackedFix(): TrackedFix | null {
  if (!T.frame || !T.f.ready) return null;
  const est = T.f.estimate(now());
  const [lat, lon] = T.frame.toLatLon(est.e, est.n);
  return { lat, lon, accuracy: est.sigma, gpsAccuracy: T.f.gpsAcc, held: est.held, motion: est.motion };
}

/** The calibration's compass correction (the walking direction uses it). */
export function setTrackHeadingOffset(deg: number): void {
  T.headingOffset = Number.isFinite(deg) ? deg : 0;
}

function token(): AnchorToken {
  return { x: [...T.f.x], P: [...T.f.P], after: [], afterPxx: 0 };
}

function sealToken(tk: AnchorToken): AnchorToken {
  tk.after = [...T.f.x];
  tk.afterPxx = T.f.P[0];
  return tk;
}

/** A calibration: the true position is the current one moved (de, dn). */
export function anchorShift(de: number, dn: number, sigma: number): AnchorToken | null {
  if (!T.f.ready) return null;
  const tk = token();
  T.f.anchorShift(now(), de, dn, sigma);
  return sealToken(tk);
}

/** A kerb calibration: moved `shift` metres along (ue, un) only. */
export function anchorAlong(ue: number, un: number, shift: number, sigma: number): AnchorToken | null {
  if (!T.f.ready) return null;
  const tk = token();
  T.f.anchorAlong(now(), ue, un, shift, sigma);
  return sealToken(tk);
}

/** Undo an anchor: the state before it, plus the movement since. */
export function undoAnchor(tk: AnchorToken): void {
  const f = T.f;
  f.predict(now());
  const de = f.x[0] - tk.after[0], dn = f.x[1] - tk.after[1];
  const grow = Math.max(0, f.P[0] - tk.afterPxx);
  f.x = [tk.x[0] + de, tk.x[1] + dn, tk.x[2], tk.x[3]];
  f.P = [...tk.P];
  f.P[0] += grow;
  f.P[5] += grow;
}

/** Calibration reset: forget what the anchor taught the filter, so it
 * re-converges on GPS. */
export function forgetAnchor(): void {
  const f = T.f;
  if (!f.ready) return;
  f.predict(now());
  const acc = f.gpsAcc ?? 8;
  for (let k = 0; k < 16; k++) if ((Math.floor(k / 4) < 2) !== (k % 4 < 2)) f.P[k] = 0;
  f.x[2] = f.x[3] = 0;
  f.P[0] += acc * acc;
  f.P[5] += acc * acc;
}

export function setHoldPosition(on: boolean): void {
  T.heldSteps = 0;
  T.f.setHold(on, now());
}

export function holdingPosition(): boolean {
  return T.f.held;
}

export function onTrackEvent(fn: (e: "hold-released") => void): () => void {
  events.add(fn);
  return () => events.delete(fn);
}
